import React from 'react';
import Layout from '@/components/layout/Layout';
import { SEOHead } from '@/components/shared/SEOHead';
import { Cookie } from 'lucide-react';

const CookiePolicyPage = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Cookie Policy",
    "description": "Cookie Policy for Redspire Civil Engineering website - Learn about cookies and tracking technologies we use.",
    "url": "https://redspire.com/cookie-policy",
    "publisher": {
      "@type": "Organization",
      "name": "Redspire Civil Engineering"
    }
  };

  return (
    <Layout>
      <SEOHead
        title="Cookie Policy | Redspire Civil Engineering"
        description="Learn about cookies and tracking technologies used on the Redspire Civil Engineering website and how to manage your cookie preferences."
        keywords={['cookie policy', 'cookies', 'tracking technologies', 'privacy']}
        url="https://redspire.com/cookie-policy"
        schema={schema}
      />

      <div className="bg-gradient-to-br from-gray-900 to-gray-800 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-4">
            <Cookie className="w-12 h-12 text-red-500" />
            <h1 className="text-4xl md:text-5xl font-bold">Cookie Policy</h1>
          </div>
          <p className="text-xl text-gray-300">How we use cookies and tracking technologies</p>
          <p className="text-sm text-gray-400 mt-4">Effective Date: January 1, 2025</p>
        </div>
      </div>

      <article className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">1. Introduction</h2>
            <p className="text-gray-700 leading-relaxed">
              This Cookie Policy explains how Redspire Civil Engineering ("Redspire," "we," "us," or "our") uses cookies and similar tracking technologies on our website (https://redspire.com). This policy should be read in conjunction with our Privacy Policy.
            </p>
            <p className="text-gray-700 leading-relaxed mt-4">
              By using our website, you consent to the use of cookies in accordance with this Cookie Policy. If you do not agree with our use of cookies, you should adjust your browser settings or refrain from using our website.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">2. What Are Cookies?</h2>
            <p className="text-gray-700 leading-relaxed">
              Cookies are small text files that are placed on your device (computer, smartphone, or tablet) when you visit a website. Cookies are widely used to make websites work more efficiently and to provide information to website owners.
            </p>
            <p className="text-gray-700 leading-relaxed mt-4">
              Cookies can be "persistent" (remaining on your device until deleted or until they expire) or "session" (temporary cookies that are deleted when you close your browser).
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">3. Types of Cookies We Use</h2>
            
            <h3 className="text-2xl font-semibold text-gray-800 mb-3 mt-6">3.1 Strictly Necessary Cookies</h3>
            <p className="text-gray-700 leading-relaxed">
              These cookies are essential for the website to function properly. They enable core functionality such as page navigation, secure areas access, and form submission. The website cannot function properly without these cookies.
            </p>
            <div className="bg-gray-50 p-4 rounded-lg mt-3">
              <p className="text-sm text-gray-700"><strong>Examples:</strong> Session management, security tokens, load balancing</p>
              <p className="text-sm text-gray-700 mt-2"><strong>Duration:</strong> Session or up to 24 hours</p>
              <p className="text-sm text-gray-700 mt-2"><strong>Can be disabled:</strong> No (required for website functionality)</p>
            </div>

            <h3 className="text-2xl font-semibold text-gray-800 mb-3 mt-6">3.2 Performance and Analytics Cookies</h3>
            <p className="text-gray-700 leading-relaxed">
              These cookies collect information about how visitors use our website, such as which pages are visited most often and whether visitors receive error messages. This information helps us improve how our website works.
            </p>
            <div className="bg-gray-50 p-4 rounded-lg mt-3">
              <p className="text-sm text-gray-700"><strong>Examples:</strong> Google Analytics, page load times, error tracking</p>
              <p className="text-sm text-gray-700 mt-2"><strong>Duration:</strong> Up to 2 years</p>
              <p className="text-sm text-gray-700 mt-2"><strong>Can be disabled:</strong> Yes (via browser settings or opt-out)</p>
            </div>

            <h3 className="text-2xl font-semibold text-gray-800 mb-3 mt-6">3.3 Functionality Cookies</h3>
            <p className="text-gray-700 leading-relaxed">
              These cookies allow our website to remember choices you make (such as your preferred language or region) and provide enhanced, personalized features.
            </p>
            <div className="bg-gray-50 p-4 rounded-lg mt-3">
              <p className="text-sm text-gray-700"><strong>Examples:</strong> Language preferences, font size, accessibility settings</p>
              <p className="text-sm text-gray-700 mt-2"><strong>Duration:</strong> Session or up to 1 year</p>
              <p className="text-sm text-gray-700 mt-2"><strong>Can be disabled:</strong> Yes (but may affect website experience)</p>
            </div>

            <h3 className="text-2xl font-semibold text-gray-800 mb-3 mt-6">3.4 Targeting/Advertising Cookies</h3>
            <p className="text-gray-700 leading-relaxed">
              We currently do not use advertising cookies. If we implement advertising or marketing cookies in the future, we will update this policy and request your consent where required.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">4. Third-Party Cookies</h2>
            <p className="text-gray-700 leading-relaxed">
              Some cookies on our website are set by third-party services we use to enhance functionality or analyze site usage:
            </p>
            
            <h3 className="text-2xl font-semibold text-gray-800 mb-3 mt-6">4.1 Google Analytics</h3>
            <p className="text-gray-700 leading-relaxed">
              We use Google Analytics to understand how visitors interact with our website. Google Analytics uses cookies to collect information about website usage, which is transmitted to and stored by Google. This information helps us improve our website and services.
            </p>
            <p className="text-gray-700 leading-relaxed mt-3">
              You can opt out of Google Analytics by installing the Google Analytics Opt-out Browser Add-on: <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-red-600 hover:underline">https://tools.google.com/dlpage/gaoptout</a>
            </p>

            <h3 className="text-2xl font-semibold text-gray-800 mb-3 mt-6">4.2 Social Media Plugins</h3>
            <p className="text-gray-700 leading-relaxed">
              Our website may include social media features (such as LinkedIn, Twitter, Facebook share buttons). These features may set cookies to enable proper functionality. Social media companies may collect information about your visit to our website.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">5. How to Control Cookies</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              You have several options to control and manage cookies:
            </p>

            <h3 className="text-2xl font-semibold text-gray-800 mb-3 mt-6">5.1 Browser Settings</h3>
            <p className="text-gray-700 leading-relaxed">
              Most web browsers allow you to control cookies through their settings. You can typically:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-3">
              <li>View and delete cookies currently stored</li>
              <li>Block third-party cookies</li>
              <li>Block all cookies</li>
              <li>Delete all cookies when you close your browser</li>
            </ul>

            <p className="text-gray-700 leading-relaxed mt-4">
              Common browsers and their cookie management pages:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-3">
              <li><strong>Google Chrome:</strong> Settings &gt; Privacy and security &gt; Cookies and other site data</li>
              <li><strong>Mozilla Firefox:</strong> Options &gt; Privacy & Security &gt; Cookies and Site Data</li>
              <li><strong>Safari:</strong> Preferences &gt; Privacy &gt; Cookies and website data</li>
              <li><strong>Microsoft Edge:</strong> Settings &gt; Cookies and site permissions</li>
            </ul>

            <h3 className="text-2xl font-semibold text-gray-800 mb-3 mt-6">5.2 Do Not Track Signals</h3>
            <p className="text-gray-700 leading-relaxed">
              Some browsers include a "Do Not Track" (DNT) feature that signals to websites that you do not want your online activity tracked. Our website currently does not respond to DNT signals, but we respect your right to control tracking through browser and opt-out mechanisms.
            </p>

            <h3 className="text-2xl font-semibold text-gray-800 mb-3 mt-6">5.3 Mobile Devices</h3>
            <p className="text-gray-700 leading-relaxed">
              Mobile devices may have settings to limit ad tracking or reset your advertising identifier. Consult your device's help documentation for specific instructions.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">6. Impact of Disabling Cookies</h2>
            <p className="text-gray-700 leading-relaxed">
              Disabling cookies may affect your experience on our website. Specifically:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-3">
              <li>You may not be able to access certain areas or features</li>
              <li>Form submissions may not function properly</li>
              <li>Your preferences may not be saved between visits</li>
              <li>Website performance and functionality may be degraded</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              Strictly necessary cookies cannot be disabled if you want to use the website, as they are essential for basic functionality and security.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">7. Cookie Lifespan</h2>
            <p className="text-gray-700 leading-relaxed">
              Cookies have different lifespans depending on their purpose:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-3">
              <li><strong>Session Cookies:</strong> Deleted when you close your browser</li>
              <li><strong>Persistent Cookies:</strong> Remain on your device for a set period or until manually deleted</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              Most of our cookies are set to expire within 24 months, though some analytical cookies may last up to 2 years. You can delete cookies at any time through your browser settings.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">8. Updates to Cookie Usage</h2>
            <p className="text-gray-700 leading-relaxed">
              We may introduce new cookies or change how we use cookies as we improve and expand our website. When we make significant changes to our cookie practices, we will update this Cookie Policy and notify visitors as appropriate.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">9. More Information</h2>
            <p className="text-gray-700 leading-relaxed">
              For more information about online privacy and cookies, you may find these resources helpful:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-3">
              <li>All About Cookies: <a href="https://www.allaboutcookies.org" target="_blank" rel="noopener noreferrer" className="text-red-600 hover:underline">www.allaboutcookies.org</a></li>
              <li>Network Advertising Initiative: <a href="https://www.networkadvertising.org" target="_blank" rel="noopener noreferrer" className="text-red-600 hover:underline">www.networkadvertising.org</a></li>
              <li>Your Online Choices: <a href="https://www.youronlinechoices.com" target="_blank" rel="noopener noreferrer" className="text-red-600 hover:underline">www.youronlinechoices.com</a></li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">10. Contact Us</h2>
            <p className="text-gray-700 leading-relaxed">
              If you have questions about our use of cookies or this Cookie Policy, please contact us:
            </p>
            <div className="bg-gray-50 p-6 rounded-lg mt-4">
              <p className="text-gray-900 font-semibold mb-2">Redspire Civil Engineering</p>
              <p className="text-gray-700">218 Brookside Industrial Park</p>
              <p className="text-gray-700">Stamford, CT 06902</p>
              <p className="text-gray-700 mt-3">Email: <a href="mailto:info@redspire.com" className="text-red-600 hover:underline">info@redspire.com</a></p>
              <p className="text-gray-700">Phone: <a href="tel:+12034963375" className="text-red-600 hover:underline">+1 (203) 496-3375</a></p>
            </div>
          </section>

          <div className="border-t border-gray-200 pt-8 mt-12">
            <p className="text-sm text-gray-600">
              Last Updated: January 1, 2025
            </p>
            <p className="text-sm text-gray-600 mt-2">
              © 2025 Redspire Civil Engineering. All rights reserved.
            </p>
          </div>

        </div>
      </article>
    </Layout>
  );
};

export default CookiePolicyPage;
